import { useIsAllowed } from '@/app/navigation/access';
import { createLink } from '@/app/navigation/stack-links';
import {
  useIsPathActive,
  useNavigationKey,
} from '@/app/navigation/useActivePath';
import {
  NavRowContextMenu,
  PinToggleAction,
} from '@/components/Sidebar/nav-pin-controls';
import { NavItem } from '@/components/Sidebar/sidebar.types';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@/components/ui/Sidebar/sidebar';
import { navIconName } from '@/features/pinned-nav/pinned-nav.icons';
import { useCurrentUser } from '@/features/users/users.hooks';
import { decode } from '@certhon/cloud-permissions';
import { ChevronRight, ExternalLink } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

export function NavMain({ items }: { items: NavItem[] }) {
  // Screens closed to this account are hidden, not disabled — an external
  // account should not see the shape of the internal application.
  const { filterNavItems } = useIsAllowed();
  const visibleItems = useMemo(
    () => filterNavItems(items),
    [filterNavItems, items],
  );

  return (
    <SidebarGroup>
      <SidebarGroupLabel>Applications</SidebarGroupLabel>
      <SidebarMenu>
        {visibleItems.map((item) => (
          <SidebarNavItem key={item.title} item={item} />
        ))}
      </SidebarMenu>
    </SidebarGroup>
  );
}

const SidebarMenuLink = createLink(SidebarMenuButton);
const SidebarMenuSubLink = createLink(SidebarMenuSubButton);

function SidebarNavItem({ item }: { item: NavItem }) {
  const isActive = useIsPathActive();

  if (item.url) {
    const pin = {
      url: item.url,
      title: item.title,
      icon: navIconName(item.icon),
    };
    return (
      <NavRowContextMenu item={pin}>
        <SidebarMenuItem>
          <div className="flex">
            <SidebarMenuLink
              to={item.url}
              tooltip={item.title}
              isActive={isActive(item.url)}
            >
              {item.icon && <item.icon />}
              <span>{item.title}</span>
            </SidebarMenuLink>
          </div>
          <PinToggleAction item={pin} />
        </SidebarMenuItem>
      </NavRowContextMenu>
    );
  }

  return <SidebarNavGroup item={item} />;
}
/**
 * Every sub-item stays visible; one whose `permission` the user lacks is
 * rendered disabled, so the menu shows the whole application.
 */
function useSubItemAccess(item: NavItem) {
  const currentUser = useCurrentUser();
  return useMemo(() => {
    const permissions = currentUser
      ? decode(currentUser.permissions)
      : undefined;
    return (item.items ?? []).map((sub) => ({
      ...sub,
      disabled: !!sub.permission && !permissions?.[sub.permission],
    }));
  }, [item.items, currentUser]);
}

function SidebarNavGroup({ item }: { item: NavItem }) {
  const isActive = useIsPathActive();
  const subItems = useSubItemAccess(item);
  // External rows (href) never count: the group's own link stays in-app.
  const firstUrl =
    subItems.find((sub) => sub.url && !sub.disabled)?.url ?? null;
  const groupIsActive = subItems.some((sub) => isActive(sub.url));

  // Tri-state, sitting between "the user decides" and "groups fold themselves
  // away so the menu stays readable":
  //   null  — follow the active page (the state every group starts in)
  //   true  — pinned open by the user
  //   false — closed by the user
  // Navigating only clears the `false` state, so a group the user closed stops
  // haunting them, while one they deliberately opened stays open.
  const [expanded, setExpanded] = useState<boolean | null>(null);
  const open = expanded ?? groupIsActive;

  const navigationKey = useNavigationKey();
  useEffect(() => {
    setExpanded((current) => (current === false ? null : current));
  }, [navigationKey]);

  return (
    <Collapsible
      key={item.title}
      asChild
      open={open}
      onOpenChange={setExpanded}
      className="group/collapsible"
    >
      <SidebarMenuItem>
        <div className="flex">
          {firstUrl ? (
            <SidebarMenuLink
              to={firstUrl}
              tooltip={item.title}
              isActive={!open && groupIsActive}
            >
              {item.icon && <item.icon />}
              <span>{item.title}</span>
            </SidebarMenuLink>
          ) : (
            // Nothing in-app to open (empty, or only links to the previous
            // application): the heading only folds the group open and shut.
            <CollapsibleTrigger asChild>
              <SidebarMenuButton tooltip={item.title}>
                {item.icon && <item.icon />}
                <span>{item.title}</span>
              </SidebarMenuButton>
            </CollapsibleTrigger>
          )}
          <CollapsibleTrigger asChild>
            <SidebarMenuButton
              tooltip={item.title}
              square
              className="group-data-[collapsible=icon]:hidden"
            >
              <ChevronRight
                className="ml-auto transition-transform duration-200
                  group-data-[state=open]/collapsible:rotate-90"
              />
            </SidebarMenuButton>
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent>
          <SidebarMenuSub>
            {subItems.map((subItem) => (
              <SidebarNavSubItem
                key={subItem.title}
                title={subItem.title}
                url={subItem.url}
                href={subItem.href}
                disabled={subItem.disabled}
                active={isActive(subItem.url)}
                icon={navIconName(item.icon)}
              />
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}

function SidebarNavSubItem({
  title,
  url,
  href,
  disabled = false,
  active,
  icon,
}: {
  title: string;
  url?: string;
  /** Screen in the previous application; opens in a new tab. */
  href?: string;
  /** The user lacks the permission for this screen. */
  disabled?: boolean;
  active: boolean;
  /** Icon of the parent group — pinned sub-items borrow it, see PinnedRow. */
  icon?: string;
}) {
  if (href && !disabled) {
    // Lives in the previous application: a plain external link, never
    // active and not pinnable (pins are in-app URLs).
    return (
      <SidebarMenuSubItem>
        <SidebarMenuSubButton
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          title="Opens the previous application"
          className="md:pr-8"
        >
          <span className="truncate">{title}</span>
          <ExternalLink className="ml-auto size-3.5! shrink-0 opacity-60" />
        </SidebarMenuSubButton>
      </SidebarMenuSubItem>
    );
  }

  if (!url || disabled) {
    // A screen that does not exist yet, or one this user may not open.
    return (
      <SidebarMenuSubItem>
        <SidebarMenuSubButton
          aria-disabled
          title={
            disabled
              ? 'You do not have permission to access this section'
              : undefined
          }
        >
          <span>{title}</span>
        </SidebarMenuSubButton>
      </SidebarMenuSubItem>
    );
  }

  const pin = { url, title, icon };
  return (
    <NavRowContextMenu item={pin}>
      <SidebarMenuSubItem>
        <SidebarMenuSubLink to={url} isActive={active} className="md:pr-8">
          <span className="truncate">{title}</span>
        </SidebarMenuSubLink>
        <PinToggleAction item={pin} sub />
      </SidebarMenuSubItem>
    </NavRowContextMenu>
  );
}
